import { ref, computed } from 'vue';
import constants from '@/utilities/constants';
import {
  NoteContentType,
  NoteMetadata,
  NoteTemplate,
  notesCalendarUsedEventIdsKey,
} from '@/types/notes';
import { translate } from '@/utilities';
import useAudioTranscription from '@/utilities/composables/useAudioTranscription';
import { useStore } from 'vuex';
import getTextToTitleCase from '@/utilities/getTextToTitleCase';
import {
  convertTimeTo24Hours,
  convertDateToDDMM,
  convertToHoursMinutes,
} from '@/utilities/timeDifference';
import { browserStorageSetItem, browserStorageGetItem } from '@/utilities/browser-storage.util';
import { nanoid } from 'nanoid';
import { useRouter } from 'vue-router';
import { OnboardingActionType } from '@/types/onboardingActions';
import useToggleSidebar from '@/utilities/composables/useToggleSidebar';

const LARGE_SCREEN_BREAKPOINT = 2200;

export default function useNotes() {
  const store = useStore();
  const router = useRouter();

  const noteId = ref();

  const { currentTranscriptionNoteId, stopRecording, startRecording } =
    useAudioTranscription(noteId);

  const { closeSidebar } = useToggleSidebar();

  const debouncedUpdateNoteTitle = ref();
  const debouncedUpdateNoteContent = ref();

  const noteTemplates = computed(() => {
    return store.getters.getNoteTemplates;
  });

  const noteTemplatesFavourited = computed(() => {
    const templates = noteTemplates.value;

    const favouritedTemplates = Object.fromEntries(
      Object.entries(templates).filter(([, value]: [any, any]) => value.is_favourited)
    );

    const autoEnhancedTemplate = {
      id: NoteTemplate.AutoEnhanced,
      title: translate('autoEnhanced'),
      icon: {
        src: 'zap',
        color: 'var(--iconDefault)',
        type: 'icon',
      },
      is_favourited: true,
    };

    const result = {
      [NoteTemplate.AutoEnhanced]: autoEnhancedTemplate,
      ...favouritedTemplates,
    };

    return result;
  });

  const getNoteEditorId = (noteId: string) => {
    const teamId = store.getters.getTeamID;
    return `${teamId}-editor-note-${noteId}`;
  };

  const debounceUpdateNoteTitle = (
    noteId: string,
    title: string,
    immediate = false,
    afterUpdateCallback?: () => void
  ) => {
    clearTimeout(debouncedUpdateNoteTitle.value);

    if (immediate) {
      store.dispatch('updateNoteTitle', { noteId, title });
      return;
    }

    debouncedUpdateNoteTitle.value = setTimeout(() => {
      store.dispatch('updateNoteTitle', { noteId, title });

      clearTimeout(debouncedUpdateNoteTitle.value);
      debouncedUpdateNoteTitle.value = null;

      afterUpdateCallback?.();
    }, 1000);
  };

  const updateCorrectNoteContent = (noteId: string, content: string, aiNoteId: string = '') => {
    const action = aiNoteId ? 'updateAiNoteContent' : 'updateNoteContent';
    store.dispatch(action, {
      noteId,
      ...(aiNoteId ? { aiNoteId } : { fieldType: NoteContentType.UserNotes }),
      content,
    });
  };

  const debounceUpdateNoteContent = (noteId: string, content: string, aiNoteId: string = '') => {
    clearTimeout(debouncedUpdateNoteContent.value);

    debouncedUpdateNoteContent.value = setTimeout(() => {
      updateCorrectNoteContent(noteId, content, aiNoteId);

      clearTimeout(debouncedUpdateNoteContent.value);
      debouncedUpdateNoteContent.value = null;
    }, 1000);
  };

  const clearUpdateNoteDebounceTimeout = ({
    noteId,
    title,
    content,
    aiNoteId = '',
  }: {
    noteId: string;
    title: string;
    content: string;
    aiNoteId: string;
  }) => {
    if (debouncedUpdateNoteTitle.value) {
      clearTimeout(debouncedUpdateNoteTitle.value);
      debouncedUpdateNoteTitle.value = null;

      store.dispatch('updateNoteTitle', { noteId, title });
    }

    if (debouncedUpdateNoteContent.value) {
      updateCorrectNoteContent(noteId, content, aiNoteId);

      clearTimeout(debouncedUpdateNoteContent.value);
      debouncedUpdateNoteContent.value = null;
    }
  };

  const createNote = async ({
    title = '',
    calendarEvent = {},
    localId = constants.newNote,
    isOnboardingNote = false,
    fromMeetingNotification = false,
    afterLocalNoteCreatedCallback = () => {},
  }: {
    title?: string;
    calendarEvent?: any;
    localId?: string;
    isOnboardingNote?: boolean;
    fromMeetingNotification?: boolean;
    afterLocalNoteCreatedCallback?: () => void;
  }) => {
    const localNote = {
      id: localId,
      title,
      time_created: Date.now() / 1000,
      ...(Object.keys(calendarEvent).length && {
        meeting_date: calendarEvent.start,
        meeting_time: calendarEvent.start,
        meeting_metadata: {
          links: calendarEvent.meeting_links,
        },
      }),
    };

    store.dispatch('setNote', { note: localNote });
    afterLocalNoteCreatedCallback();

    const newNoteId = await store.dispatch('createNewNote', {
      title,
      calendarEvent,
      noteIdToUpdate: localId,
      isOnboardingNote,
      fromMeetingNotification,
    });

    const oldEditorId = getNoteEditorId(constants.newNote);
    const newEditorId = getNoteEditorId(newNoteId);

    store.dispatch('updateEditorId', {
      oldId: oldEditorId,
      newId: newEditorId,
      resourceId: newNoteId,
    });

    return newNoteId;
  };

  const getNoteChronologicalAfterNoteId = (noteId: string, notesList: Array<any>) => {
    if (noteId === constants.newNote) return notesList?.[1];

    const noteIndex = notesList.findIndex((note: any) => note.id === noteId);

    const noteBefore = notesList[noteIndex - 1];
    const noteAfter = notesList[noteIndex + 1];

    if (noteIndex === -1) return null;
    if (!noteAfter && noteBefore) return noteBefore;
    return noteAfter;
  };

  const deleteNote = (noteId: string) => {
    if (currentTranscriptionNoteId.value === noteId) stopRecording();
    store.dispatch('deleteNote', noteId);
  };

  const updateNoteTitle = (
    noteId: string,
    title: string,
    immediate: boolean = false,
    afterUpdateCallback?: () => void
  ) => {
    store.dispatch('localUpdateNoteProp', {
      noteId,
      propertyName: 'title',
      value: title,
    });

    debounceUpdateNoteTitle(noteId, title, immediate, afterUpdateCallback);
  };

  const updateNoteContent = (noteId: string, content: string, aiNoteId: string = '') => {
    store.dispatch('localUpdateNoteProp', {
      noteId,
      ...(aiNoteId && { aiNoteId }),
      propertyName: aiNoteId ? 'content' : NoteContentType.UserNotes,
      value: content,
    });

    debounceUpdateNoteContent(noteId, content, aiNoteId);
  };

  const getNote = (noteId: string) => store.getters.getNotes[noteId] || {};

  const getAiNote = (noteId: string, templateId: string) => {
    return getNote(noteId)[NoteContentType.AiNotes]?.find(
      (note: any) => note?.note_template_id === templateId
    );
  };

  const updateLastNoteTemplateId = (noteId: string, templateId: string) => {
    if (!templateId) return;

    // Prevent updating if the last note template is the same
    if (getNote(noteId).last_note_template_id === templateId) return;

    store.dispatch('localUpdateNoteProp', {
      noteId,
      propertyName: 'last_note_template_id',
      value: templateId,
    });

    store.dispatch('updateLastNoteTemplateId', { noteId, templateId });
  };

  const getNoteHasAiNotes = (noteId: string) => {
    return getNote(noteId)[NoteContentType.AiNotes]?.length > 0;
  };

  const getLastNoteTemplate = (noteId: string) => {
    return getNote(noteId)?.last_note_template_id || '';
  };

  const getPublicNoteUrl = (noteId: string) => {
    return getNote(noteId).public_settings?.url || '';
  };

  const togglePublicNote = async (noteId: string) => {
    const isPublicNote = !getPublicNoteUrl(noteId);

    const updatedNote = await store.dispatch('updateNote', {
      noteId,
      payload: { is_public: isPublicNote },
    });

    store.dispatch('localUpdateNoteProp', {
      noteId,
      propertyName: 'is_public',
      value: isPublicNote,
    });

    store.dispatch('localUpdateNoteProp', {
      noteId,
      propertyName: 'public_settings',
      value: {
        url: isPublicNote ? updatedNote?.public_settings?.url : '',
      },
    });
  };

  const getNoteMeetingLinkInfo = (noteId: string) => {
    const linkInfo = getNote(noteId).meeting_metadata?.links?.[0] || {};

    return {
      link: linkInfo.link || '',
      provider: getTextToTitleCase(linkInfo.provider || ''),
    };
  };

  const usedEventIds = ref<string[]>(browserStorageGetItem(notesCalendarUsedEventIdsKey) || []);

  const markEventAsUsed = (eventId: string) => {
    usedEventIds.value.push(eventId);
    browserStorageSetItem(notesCalendarUsedEventIdsKey, usedEventIds.value);
  };

  const getNoteTitleFromEvent = (event: any): string => {
    if (event.id === constants.noteOnboardingEventId) return translate('get_started_notes.title');

    const eventStartDate = new Date(event.start * 1000);
    const eventEndDate = new Date(event.end * 1000);
    const formattedStartDate = convertTimeTo24Hours(eventStartDate);

    // If event is not today, add the day and month to the end time
    const formattedEndDate =
      convertTimeTo24Hours(eventEndDate) +
      (eventEndDate.toDateString() !== new Date().toDateString()
        ? `, ${convertDateToDDMM(event.end)}`
        : '');

    return `${event.title || translate('untitled')} (${formattedStartDate} - ${formattedEndDate})`;
  };

  const calendarEvents = computed(() => {
    return store.getters.getUpcomingGoogleCalendarEvents;
  });

  const createNoteFromEvent = async (eventId: string, fromMeetingNotification: boolean = false) => {
    const calendarEvent = calendarEvents.value.find((e: any) => e.id === eventId);
    if (!calendarEvent) return;

    markEventAsUsed(eventId);

    const title = calendarEvent.title ?? translate('untitled');
    const localId = nanoid(10);
    const id = await createNote({
      title,
      calendarEvent,
      localId,
      fromMeetingNotification,
      afterLocalNoteCreatedCallback: () => {
        router.push({
          name: constants.routeNames.note,
          params: { noteId: localId },
        });
      },
    });
    router.push({ name: constants.routeNames.note, params: { noteId: id } });

    // if event has meeting link, open it in new tab
    const { link } = calendarEvent.meeting_links?.[0] ?? {};
    if (link) {
      window.open(link, '_blank');
    }
  };

  const createNoteFromOnboardingEvent = async () => {
    const localId = nanoid(10);
    const id = await createNote({
      title: translate('get_started_notes.title'),
      localId,
      isOnboardingNote: true,
      afterLocalNoteCreatedCallback: () => {
        router.push({
          name: constants.routeNames.note,
          params: { noteId: localId },
        });
      },
    });
    router.push({
      name: constants.routeNames.note,
      params: { noteId: id },
      query: { fromTutorial: 'true' },
    });

    noteId.value = id;

    store.dispatch('completeOnboardingActions', [OnboardingActionType.GetStartedNotes]);
    startRecording();
    closeSidebar(LARGE_SCREEN_BREAKPOINT);

    window.electron.send('notes-onboarding-channel', {
      path: constants.electronNotesOnboardingRoute.path,
    });
  };

  const getSuggestedActionById = (id: string) => {
    // loop through notes, and ai_notes of notes, look for actions that have the id
    const notes = store.getters.getNotes;
    const aiNotes = Object.values(notes).flatMap((note: any) => note[NoteContentType.AiNotes]);
    const actions = aiNotes.flatMap((note: any) => note.actions);
    return actions.find((action: any) => action?.id === id);
  };

  const getNoteMetadata = (noteId: string): NoteMetadata => {
    const note = getNote(noteId);

    const meetingAttendees = [...(note?.meeting_attendees || [])];
    const meUser = store.getters.getUser;

    meetingAttendees.sort((a: any, b: any) => {
      /**
       * Move me user to the end of the list,
       * and move users that are not in the workspace to the start
       */
      if (a.user_id === meUser.id || !b.user_id) return 1;
      if (b.user_id === meUser.id || !a.user_id) return -1;

      // For users in the workspace, sort by email
      return a.email.localeCompare(b.email);
    });

    return {
      meetingAttendees,
      meetingStartDate: note?.meeting_date || null,
      meetingStartTime: note?.meeting_time || null,
    };
  };

  const getNoteMeetingStartTime = (noteId: string) => {
    const metadata = getNoteMetadata(noteId);
    if (!metadata?.meetingStartTime) return convertToHoursMinutes(new Date());

    return convertToHoursMinutes(new Date(+metadata.meetingStartTime * 1000));
  };

  const updateNoteMetadataAttendees = ({
    noteId,
    attendee,
    removeAttendee = false,
    local = false,
    oldAttendees = [],
  }: {
    noteId: string;
    attendee: any;
    removeAttendee?: boolean;
    local?: boolean;
    oldAttendees?: any[];
  }) => {
    const note = getNote(noteId);
    const meetingAttendees = note?.meeting_attendees || [];

    if (!local) {
      const newAttendees = [
        ...meetingAttendees.map((a: any) => ({
          email: a.email,
          name: a.display_name || a.email,
        })),
      ];

      // Prevent updating if the attendees are the same
      if (oldAttendees.length === newAttendees.length) {
        if (
          oldAttendees.every((oldAttendee: any) =>
            newAttendees.find((newAttendee) => oldAttendee.email === newAttendee.email)
          )
        )
          return;
      }

      // Make sure the meeting date has the correct time if it exists
      if (note?.meeting_time && note?.meeting_date) {
        const time = new Date(+note.meeting_time * 1000);
        const date = new Date(+note.meeting_date * 1000);
        date.setHours(time.getHours(), time.getMinutes());
        note.meeting_date = date.getTime() / 1000;
      }

      const googleCalendarEvent = {
        start: note?.meeting_date || null,
        attendees: newAttendees,
      };

      store.dispatch('updateNote', {
        noteId,
        payload: {
          google_calendar_event: googleCalendarEvent,
        },
      });
      return;
    }

    const newValue = removeAttendee
      ? meetingAttendees.filter((a: any) => a.email !== attendee.email)
      : [
          ...meetingAttendees,
          {
            user_id: attendee.id,
            email: attendee.email,
            name: attendee.display_name,
          },
        ];

    store.dispatch('localUpdateNoteProp', {
      noteId,
      propertyName: 'meeting_attendees',
      value: newValue,
    });
  };

  const updateNote = ({
    noteId,
    localField,
    fieldToUpdate,
    value,
  }: {
    noteId: string;
    localField: string;
    fieldToUpdate: string;
    value: any;
  }) => {
    store.dispatch('localUpdateNoteProp', {
      noteId,
      propertyName: localField,
      value,
    });

    store.dispatch('updateNote', {
      noteId,
      payload: {
        [fieldToUpdate]: value,
      },
    });
  };

  return {
    getNoteEditorId,
    noteTemplates,
    noteTemplatesFavourited,
    debouncedUpdateNoteTitle,
    updateCorrectNoteContent,
    debouncedUpdateNoteContent,
    debounceUpdateNoteTitle,
    debounceUpdateNoteContent,
    createNote,
    deleteNote,
    getNoteChronologicalAfterNoteId,
    updateNoteTitle,
    updateNoteContent,
    clearUpdateNoteDebounceTimeout,
    getAiNote,
    updateLastNoteTemplateId,
    getNoteHasAiNotes,
    getLastNoteTemplate,
    getPublicNoteUrl,
    togglePublicNote,
    getNoteMeetingLinkInfo,
    usedEventIds,
    markEventAsUsed,
    getNoteTitleFromEvent,
    calendarEvents,
    createNoteFromEvent,
    createNoteFromOnboardingEvent,
    getSuggestedActionById,
    getNoteMetadata,
    updateNoteMetadataAttendees,
    updateNote,
    getNoteMeetingStartTime,
  };
}
